import * as Sentry from "@sentry/react-native";
import { useQuery } from "@tanstack/react-query";
import * as Location from "expo-location";
import { router } from "expo-router";
import Fuse from "fuse.js";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { StyleSheet } from "react-native";
import { Icon } from "~/components/Icon";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { Card } from "~/components/V2/Card";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BrandText } from "~/components/V2/Text/BrandText";
import { TextInputV2 } from "~/components/V2/TextInputV2";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import type { ClinicInstance } from "~/mobx/entities/clinics/Clinic";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { getDistanceBetweenTwoGeographicalCoordinates } from "~/utils/geoDistance";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import type { FuseResult } from "fuse.js";

export const SelectClinicSearchScreen = observer(
  function SelectClinicSearchScreen() {
    const { clinicId, isStandAloneBookingFlow, isNewUser } =
      useStandaloneBookingFlow();
    useProgressBarStep(1);
    const store = useStore();
    const errorAlert = useErrorAlertV2();

    const [lastKnownLocation, setLastKnownLocation] =
      useState<Location.LocationObject | null>(null);

    const query = useQuery({
      queryKey: [
        "allClinics",
        {
          sort: lastKnownLocation ? "geocode" : "",
          latitude: lastKnownLocation?.coords.latitude,
          longitude: lastKnownLocation?.coords.longitude,
        },
      ],
      queryFn: async () => {
        const sort = lastKnownLocation ? "geocode" : "";
        const latitude = lastKnownLocation?.coords.latitude;
        const longitude = lastKnownLocation?.coords.longitude;
        const clinics = await store.clinicsStore.getClinicList(
          sort,
          latitude,
          longitude,
        );
        return clinics;
      },
    });

    const allClinics = query.data ?? [];

    const clinicSearch = new Fuse<ClinicInstance>(allClinics, {
      minMatchCharLength: 3,
      threshold: 0.3,
      keys: ["name", "city", "zipCode"],
    });

    const [searchTerm, setSearchTerm] = useState("");

    const clinicSearchResult = clinicSearch.search(searchTerm);

    if (query.isError) {
      return (
        <View flex colorWhite>
          <View centerContent paddingHorizontal24>
            <ErrorViewV2
              error={query.error}
              title={intl.formatMessage({
                id: "m4dnTD",
                defaultMessage: "Something went wrong",
                description:
                  "Error message title when fetching clinic list fails",
              })}
            />
            <ButtonV2
              onPress={query.refetch}
              title={intl.formatMessage({
                id: "ghblYR",
                defaultMessage: "Try Again!",
                description: "Button to retry fetching clinic list",
              })}
            />
          </View>
        </View>
      );
    }

    const clinics = lastKnownLocation
      ? searchTerm
        ? clinicSearchResult
        : allClinics
      : clinicSearchResult;

    return (
      <View paddingHorizontal16 paddingVertical40 flex colorWhite>
        <BrandText sizeH1 weightExtraBold colorBlack alignCenter>
          <FormattedMessage
            id="3QGdyf"
            defaultMessage="Choose a clinic"
            description="Title on the select clinic screen in booking flow"
          />
        </BrandText>
        <Spacer extraLarge />
        <TextInputV2
          label={intl.formatMessage({
            id: "f2BPDb",
            defaultMessage: "Enter city, post code...",
            description: "Placeholder text for clinic search input",
          })}
          value={searchTerm}
          onChangeText={setSearchTerm}
          rightComponent={
            <Icon name="search" size={24} color={styleConstantsV2.colorBlack} />
          }
        />
        <Spacer extraLarge />
        <ButtonV2
          centerContent
          title={intl.formatMessage({
            id: "Z7fqEE",
            defaultMessage: "Show clinics near me",
            description: "Button to show clinics near user",
          })}
          secondary
          small
          alignSelfCenter
          onPress={async () => {
            const { status } =
              await Location.requestForegroundPermissionsAsync();
            console.log({ status });
            if (status === "granted") {
              const location = await Location.getCurrentPositionAsync();
              setLastKnownLocation(location);
            } else {
              alert("Location permission is required to show clinics near you");
            }
          }}
        >
          <Icon
            name="location"
            size={24}
            color={styleConstantsV2.colorPrimary}
          />
        </ButtonV2>
        <Spacer extraLarge />
        {query.isLoading ? (
          <View padding32 centerContent>
            <SpinnerV2 />
            <View style={StyleSheet.absoluteFillObject} />
          </View>
        ) : (
          <View style={{ gap: styleConstantsV2.spacing32 }}>
            {clinics.map((item) => {
              const clinic = searchTerm
                ? (item as FuseResult<ClinicInstance>).item
                : (item as ClinicInstance);
              const shouldShowComma = !!clinic.streetAddress;
              const clinicStreetAddress = clinic.streetAddress ?? "";
              const clinicZipCode = clinic.zipCode ?? "";
              const clinicCity = clinic.city ?? "";
              const userCoordinates = lastKnownLocation?.coords;
              const distance = getDistanceBetweenTwoGeographicalCoordinates(
                userCoordinates?.latitude,
                userCoordinates?.longitude,
                clinic?.latitude,
                clinic?.longitude,
              );
              return (
                <Card
                  key={clinic.id}
                  title={clinic.name}
                  subtitle={`${clinicStreetAddress}${shouldShowComma ? ", " : ""}${clinicZipCode} ${clinicCity}`}
                  onPress={async () => {
                    try {
                      store.bookingStore.setClinicId(clinic.id);

                      let numberOfPets = 0;
                      if (!isNewUser) {
                        const pets = await store.petsStore.getMyPets();
                        numberOfPets = pets.length;
                      }

                      if (isStandAloneBookingFlow) {
                        // Log the event to the booking data layer
                        // if user has last known location and search term is empty we will assume user has selected clinic by location
                        // if user has search term we will assume user has selected clinic by search
                        if (lastKnownLocation !== null && !searchTerm.length) {
                          bookingDataLayerPush({
                            event: "Booking_web_app",
                            ga_action: "Choose clinic by location",
                            ga_label: clinic.name,
                          });
                        } else {
                          bookingDataLayerPush({
                            event: "Booking_web_app",
                            ga_action: "Choose clinic by search",
                            ga_label: clinic.name,
                          });
                        }

                        if (isNewUser || numberOfPets === 0) {
                          router.navigate({
                            pathname: "/(standalone)/booking/add-new-pet",
                            params: {
                              clinicId,
                              isNewUser: isNewUser ? "1" : "0",
                            },
                          });
                        } else {
                          router.navigate({
                            pathname: "/(standalone)/booking/pet",
                            params: {
                              clinicId,
                              isNewUser: isNewUser ? "1" : "0",
                            },
                          });
                        }
                      } else {
                        if (numberOfPets === 0) {
                          router.navigate({
                            pathname: "/(app)/book-appointment/add-new-pet",
                          });
                        } else {
                          router.navigate({
                            pathname: "/(app)/book-appointment/pet",
                          });
                        }
                      }
                    } catch (error) {
                      console.log(
                        "Error while navigating to next screen",
                        error,
                      );
                      errorAlert(error);
                      Sentry.captureException(error);
                    }
                  }}
                  distance={distance}
                />
              );
            })}
          </View>
        )}
      </View>
    );
  },
);
